/**
 * Utilitaires pour le formatage des devises
 * Configuration spécifique pour la Tunisie (TND)
 */

// Configuration par défaut pour la Tunisie
const DEFAULT_CURRENCY = 'TND';
const DEFAULT_LOCALE = 'fr-TN';

/**
 * Applique les paramètres par défaut tunisiens
 */
function applyTunisianDefaults() {
    // Configuration globale pour les devises en Tunisie
    if (typeof window !== 'undefined' && window.Intl) {
        // Configuration côté navigateur
        try {
            // Test de support des locales tunisiennes
            new Intl.NumberFormat('fr-TN', {
                style: 'currency',
                currency: 'TND'
            });
        } catch (e) {
            console.warn('⚠️ Locale fr-TN non supportée, utilisation de fr-FR');
        }
    }
}

/**
 * Formate un montant en devise avec notation compacte (K, M)
 * @param {number} amount - Montant à formater
 * @param {string} currency - Code de la devise (par défaut: TND)
 * @returns {string} - Montant formaté avec notation compacte
 */
function formatCurrencyCompact(amount, currency = DEFAULT_CURRENCY) {
    // Gestion des valeurs nulles/undefined
    if (amount === null || amount === undefined || isNaN(amount)) {
        return `0 ${currency}`;
    }
    
    const num = Number(amount);
    
    // Cas des petits montants (< 1000)
    if (Math.abs(num) < 1000) {
        return `${num} ${currency}`;
    }
    
    // Cas des milliers (K)
    if (Math.abs(num) < 1000000) {
        const thousands = num / 1000;
        // Utiliser 1 décimale si nécessaire
        const formatted = thousands % 1 === 0 ? thousands.toString() : thousands.toFixed(1);
        return `${formatted} K ${currency}`;
    }
    
    // Cas des millions (M)
    const millions = num / 1000000;
    const formatted = millions % 1 === 0 ? millions.toString() : millions.toFixed(1);
    return `${formatted} M ${currency}`;
}

/**
 * Formate un montant en devise avec le format standard tunisien
 * @param {number} amount - Montant à formater
 * @param {string} currency - Code de la devise (par défaut: TND)
 * @returns {string} - Montant formaté
 */
function formatCurrency(amount, currency = DEFAULT_CURRENCY) {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return `0,000 ${currency}`;
    }
    
    const num = Number(amount);
    
    try {
        // Utiliser Intl.NumberFormat pour le formatage
        const formatter = new Intl.NumberFormat('fr-TN', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 3
        });
        
        return `${formatter.format(num)} ${currency}`;
    } catch (e) {
        // Fallback si Intl n'est pas supporté
        return `${num.toLocaleString()} ${currency}`;
    }
}

/**
 * Parse un montant depuis une chaîne formatée
 * @param {string} formattedAmount - Montant formaté
 * @returns {number} - Montant numérique
 */
function parseCurrency(formattedAmount) {
    if (!formattedAmount || typeof formattedAmount !== 'string') {
        return 0;
    }
    
    // Supprimer les espaces, devises et caractères non numériques
    const cleaned = formattedAmount
        .replace(/[^\d,.-]/g, '') // Garder seulement chiffres, virgules, points et tirets
        .replace(/,/g, '.'); // Remplacer virgules par points
    
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
}

/**
 * Valide si un montant est dans une plage acceptable
 * @param {number} amount - Montant à valider
 * @param {number} min - Montant minimum (par défaut: 0)
 * @param {number} max - Montant maximum (par défaut: 1000000000)
 * @returns {boolean} - True si valide
 */
function validateCurrencyAmount(amount, min = 0, max = 1000000000) {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return false;
    }
    
    const num = Number(amount);
    return num >= min && num <= max;
}

/**
 * Retourne les options de devises disponibles
 * @returns {Array} - Liste des devises avec leurs informations
 */
function getCurrencyOptions() {
    return [
        { value: 'TND', label: 'Dinar Tunisien (TND)', symbol: 'TND', country: 'TN' },
        { value: 'QAR', label: 'Riyal Qatari (QAR)', symbol: 'QAR', country: 'QA' },
        { value: 'SAR', label: 'Riyal Saoudien (SAR)', symbol: 'SAR', country: 'SA' },
        { value: 'AED', label: 'Dirham Émirati (AED)', symbol: 'AED', country: 'AE' },
        { value: 'EUR', label: 'Euro (EUR)', symbol: '€', country: 'EU' },
        { value: 'USD', label: 'Dollar US (USD)', symbol: '$', country: 'US' },
        { value: 'MAD', label: 'Dirham Marocain (MAD)', symbol: 'MAD', country: 'MA' },
        { value: 'DZD', label: 'Dinar Algérien (DZD)', symbol: 'DZD', country: 'DZ' }
    ];
}

/**
 * Retourne les options de fuseaux horaires disponibles
 * @returns {Array} - Liste des fuseaux horaires
 */
function getTimezoneOptions() {
    return [
        { value: 'Africa/Tunis', label: 'Tunis (UTC+1)', country: 'TN' },
        { value: 'Asia/Qatar', label: 'Doha (UTC+3)', country: 'QA' },
        { value: 'Asia/Riyadh', label: 'Riyad (UTC+3)', country: 'SA' },
        { value: 'Asia/Dubai', label: 'Dubai (UTC+4)', country: 'AE' },
        { value: 'Europe/Paris', label: 'Paris (UTC+1)', country: 'FR' },
        { value: 'Europe/London', label: 'Londres (UTC+0)', country: 'GB' },
        { value: 'Africa/Casablanca', label: 'Casablanca (UTC+1)', country: 'MA' },
        { value: 'Africa/Algiers', label: 'Alger (UTC+1)', country: 'DZ' },
        { value: 'America/New_York', label: 'New York (UTC-5)', country: 'US' }
    ];
}

// Export CommonJS
module.exports = {
    formatCurrencyCompact,
    formatCurrency,
    parseCurrency,
    validateCurrencyAmount,
    getCurrencyOptions,
    getTimezoneOptions,
    applyTunisianDefaults,
    DEFAULT_CURRENCY,
    DEFAULT_LOCALE
}; 